// Plain JS, Zod optional.
import { z } from "zod"; // If you don't use Zod at runtime, you can remove this import.

function getTypeName(s) {
  return s && s._def && s._def.typeName ? s._def.typeName : "";
}

function unwrap(s) {
  // Peel Optional / Nullable / Default / Effects
  // Works with plain Zod in JS too
  // If zod isn't present or schema isn't Zod, the try-catch keeps things safe
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const k = getTypeName(s);
      if (k === "ZodOptional" || k === "ZodNullable" || k === "ZodDefault") {
        s = s._def.innerType;
      } else if (k === "ZodEffects") {
        s = s._def.schema;
      } else break;
    }
    return s;
  } catch {
    return s;
  }
}

function isScalarSchema(s) {
  const k = getTypeName(s);
  return [
    "ZodString","ZodNumber","ZodBoolean","ZodDate","ZodBigInt",
    "ZodEnum","ZodLiteral","ZodNaN","ZodAny","ZodUnknown",
    "ZodUnion","ZodDiscriminatedUnion"
  ].includes(k);
}

function walkZod(schema, base, out) {
  const s = unwrap(schema);
  const k = getTypeName(s);

  if (k === "ZodObject") {
    if (base.length) out.push({ path: base.join("."), type: "object" });
    const shape = s.shape;
    Object.keys(shape).forEach((key) => walkZod(shape[key], base.concat(key), out));
  } else if (k === "ZodArray") {
    if (base.length) out.push({ path: base.join("."), type: "array" });
    const inner = unwrap(s._def.type);
    // If array of objects, describe element shape (informational)
    if (getTypeName(inner) === "ZodObject") {
      walkZod(inner, base.concat("__element"), out);
    }
  } else if (isScalarSchema(s)) {
    if (base.length) out.push({ path: base.join("."), type: "scalar" });
  } else {
    // Fallback: treat as scalar
    if (base.length) out.push({ path: base.join("."), type: "scalar" });
  }
  return out;
}

/* ---------- Fallback when Zod isn't available / fails ----------
   Builds descriptor by walking an object (e.g., RHF defaultValues) */
function walkObjectShape(obj, base, out) {
  if (obj == null) {
    if (base.length) out.push({ path: base.join("."), type: "scalar" });
    return out;
  }
  if (Array.isArray(obj)) {
    if (base.length) out.push({ path: base.join("."), type: "array" });
    // Optionally inspect first element to infer element object shape
    if (obj.length && typeof obj[0] === "object" && !Array.isArray(obj[0])) {
      walkObjectShape(obj[0], base.concat("__element"), out);
    }
    return out;
  }
  if (typeof obj === "object") {
    if (base.length) out.push({ path: base.join("."), type: "object" });
    Object.keys(obj).forEach((k) => walkObjectShape(obj[k], base.concat(k), out));
    return out;
  }
  // scalar
  if (base.length) out.push({ path: base.join("."), type: "scalar" });
  return out;
}

/**
 * Attempt to build from Zod schemas; if unavailable/empty, fallback to walking defaultValues.
 * @param {{[step:string]: any}} schemas  Zod schemas or null
 * @param {{[step:string]: any}} defaultValues RHF defaultValues tree
 */
export function buildSchemaDescriptorOrFallback(schemas, defaultValues) {
  const descriptor = {};
  let builtAny = false;

  // 1) Try Zod
  try {
    if (schemas && typeof schemas === "object") {
      Object.keys(schemas).forEach((step) => {
        const schema = schemas[step];
        if (!schema) return;
        const out = walkZod(schema, [], []).filter((e) => e.path !== "");
        descriptor[step] = out;
        if (out.length) builtAny = true;
      });
    }
  } catch {
    // ignore
  }

  // 2) Fallback to defaultValues shape
  if (!builtAny && defaultValues && typeof defaultValues === "object") {
    Object.keys(defaultValues).forEach((step) => {
      const out = walkObjectShape(defaultValues[step] || {}, [], []).filter((e) => e.path !== "");
      descriptor[step] = out;
    });
  }

  return descriptor;
}
